import { RoleResponse, TaxonomicRank } from '@careerhub/api';
import { CareerHubModuleReactContext, careerStateSelector, loadBookmarks } from '@careerhub/common';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Announced } from 'office-ui-fabric-react';
import { CardsCarousel } from '../CardsCarouselV2/CardsCarouselV2';

interface IBookmarkProps {
  handleFocus: () => void;
}

/** Displays the user's bookmarks as a card carousel in ID order */
export const Bookmarks: React.FC<IBookmarkProps> = (props: IBookmarkProps) => {
  const { roleLibraryService } = React.useContext(CareerHubModuleReactContext);
  const dispatch = useDispatch();
  const { bookmarks } = useSelector(careerStateSelector);
  const [roleDetails, setRoleDetails] = React.useState<TaxonomicRank[]>([]);
  const [announcement, setAnnouncement] = React.useState('');
  const previousBookmarkIds = React.useRef(bookmarks.map((bookmark) => bookmark.id));

  // TODO cache role details so we don't make redundant requests
  const updateRoleDetails = React.useCallback(async () => {
    const roleResponse: RoleResponse[] = await Promise.all(
      bookmarks.sort(compareBookmarks).map((bookmark) => roleLibraryService.getRoleDetails(bookmark.id))
    );
    const roles: TaxonomicRank[] = roleResponse.map((roleResponse) => {
      return { ...roleResponse, childTaxonomyCount: 0 };
    });
    setRoleDetails(roles);
  }, [bookmarks, roleLibraryService]);

  React.useEffect(() => {
    updateRoleDetails();
  }, [updateRoleDetails]);

  React.useEffect(() => {
    const currentBookmarkIds = bookmarks.map((bookmark) => bookmark.id);
    const removedBookmarkId = previousBookmarkIds.current.find((id) => !currentBookmarkIds.includes(id));
    if (removedBookmarkId) {
      const removedRole = roleDetails.find((role) => role.id === removedBookmarkId);
      setAnnouncement(
        removedRole?.name ? `${removedRole.name} removed from bookmarked` : 'Item removed from bookmarked'
      );
      props.handleFocus();
    }
    previousBookmarkIds.current = currentBookmarkIds;
  }, [bookmarks, props, roleDetails]);

  React.useEffect(() => {
    dispatch(loadBookmarks());
  }, [dispatch]);

  return (
    <>
      <Announced message={announcement} aria-live="polite" />
      {bookmarks.length > 0 ? <CardsCarousel roleCards={roleDetails} /> : <p>You have no bookmarks.</p>}
    </>
  );
};

const getRoleNumber = (roleId: string) => parseInt(roleId);

const compareBookmarks = (a: { id: string }, b: { id: string }) => getRoleNumber(a.id) - getRoleNumber(b.id);
